import React, { useState, useEffect } from 'react';
import { getData, postData } from '../Services/api';
import { toast } from 'react-toastify';

const TwoFactorModal = ({ isOpen, onClose }) => {
    const [statusLoading, setStatusLoading] = useState(true);
    const [is2FAEnabled, setIs2FAEnabled] = useState(false);
    const [setupData, setSetupData] = useState(null); // { qr_code, secret_key }
    const [verificationCode, setVerificationCode] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    const check2FAStatus = async () => {
        setStatusLoading(true);
        try {
            const res = await getData('/auth/2fa/status');
            if (res.status) {
                setIs2FAEnabled(res.two_factor_enabled);
            }
        } catch (err) {
            console.error('Error fetching 2FA status:', err);
        } finally {
            setStatusLoading(false);
        }
    };

    useEffect(() => {
        if (isOpen) {
            check2FAStatus();
            setSetupData(null);
            setVerificationCode('');
        }
    }, [isOpen]);

    const handleStartSetup = async () => {
        setActionLoading(true);
        try {
            const res = await postData('/auth/2fa/setup');
            if (res.status) {
                setSetupData({
                    qr_code: res.qr_code,
                    secret_key: res.secret_key
                });
            } else {
                toast.error(res.message || 'Failed to generate 2FA setup');
            }
        } catch (err) {
            console.error('2FA Setup Error:', err);
            toast.error(err.response?.data?.message || 'Failed to generate 2FA setup');
        } finally {
            setActionLoading(false);
        }
    };

    const handleVerifyAndEnable = async (e) => {
        e.preventDefault();
        if (!verificationCode || verificationCode.length < 6) {
            toast.error('Please enter a valid 6-digit verification code');
            return;
        }

        setActionLoading(true);
        try {
            const res = await postData('/auth/2fa/verify-enable', { code: verificationCode });
            if (res.status) {
                toast.success(res.message || '2FA Activated!');
                setIs2FAEnabled(true);
                setSetupData(null);
                setVerificationCode('');
            } else {
                toast.error(res.message || 'Verification failed');
            }
        } catch (err) {
            console.error('2FA Enable Error:', err);
            toast.error(err.response?.data?.message || 'Verification failed');
        } finally {
            setActionLoading(false);
        }
    };

    const handleDisable2FA = async () => {
        if (!window.confirm('Are you sure you want to disable 2FA for your account?')) {
            return;
        }
        setActionLoading(true);
        try {
            const res = await postData('/auth/2fa/disable', { code: verificationCode });
            if (res.status) {
                toast.success('Google 2FA has been disabled.');
                setIs2FAEnabled(false);
                setSetupData(null);
                setVerificationCode('');
            } else {
                toast.error(res.message || 'Failed to disable 2FA');
            }
        } catch (err) {
            console.error('2FA Disable Error:', err);
            toast.error(err.response?.data?.message || 'Failed to disable 2FA');
        } finally {
            setActionLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content border-0 shadow">
                    <div className="modal-header bg-primary text-white">
                        <h5 className="modal-title text-white">
                            <i className="fas fa-shield-alt me-2"></i> Google 2-Factor Authentication (2FA)
                        </h5>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>

                    <div className="modal-body p-4">
                        {statusLoading ? (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="sr-only">Loading...</span>
                                </div>
                            </div>
                        ) : is2FAEnabled ? (
                            <div className="text-center py-3">
                                <div className="text-success display-4 mb-3">
                                    <i className="fas fa-check-circle"></i>
                                </div>
                                <h4 className="fw-bold text-success">Google 2FA is Active</h4>
                                <p className="text-muted">Your account is currently protected with Google 2-Factor Authentication.</p>

                                <div className="mt-4 pt-3 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-sm rounded-pill px-4"
                                        disabled={actionLoading}
                                        onClick={handleDisable2FA}
                                    >
                                        {actionLoading ? 'Disabling...' : 'Disable 2FA'}
                                    </button>
                                </div>
                            </div>
                        ) : !setupData ? (
                            <div className="text-center py-3">
                                <div className="text-primary display-4 mb-3">
                                    <i className="fas fa-qrcode"></i>
                                </div>
                                <h5>Protect Your Account with Google Authenticator</h5>
                                <p className="text-muted small">
                                    Add an extra layer of security to your login by requiring a 6-digit code generated by the Google Authenticator app on your phone.
                                </p>
                                <button
                                    type="button"
                                    className="btn btn-primary rounded-pill px-4 py-2 mt-2 shadow-sm"
                                    disabled={actionLoading}
                                    onClick={handleStartSetup}
                                >
                                    {actionLoading ? (
                                        <span><i className="fas fa-spinner fa-spin me-1"></i> Generating QR...</span>
                                    ) : (
                                        <span><i className="fas fa-key me-1"></i> Setup Google Authenticator</span>
                                    )}
                                </button>
                            </div>
                        ) : (
                            <div>
                                <h6 className="fw-bold mb-2">Step 1: Scan QR Code</h6>
                                <p className="text-muted small mb-3">
                                    Open the <strong>Google Authenticator</strong> app on your smartphone, tap <strong>+</strong>, and scan this QR code:
                                </p>

                                <div className="text-center my-3 p-3 bg-light rounded">
                                    <img src={setupData.qr_code} alt="2FA QR Code" style={{ width: '180px', height: '180px' }} />
                                    <div className="mt-2 small text-muted">
                                        Manual Key: <code className="user-select-all fw-bold text-primary">{setupData.secret_key}</code>
                                    </div>
                                </div>

                                <h6 className="fw-bold mt-4 mb-2">Step 2: Enter 6-Digit Code</h6>
                                <form onSubmit={handleVerifyAndEnable}>
                                    <div className="form-group mb-3">
                                        <input
                                            type="text"
                                            className="form-control form-control-lg text-center letter-spacing-2 fw-bold"
                                            placeholder="000000"
                                            maxLength="6"
                                            value={verificationCode}
                                            onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                                            required
                                        />
                                    </div>
                                    <div className="d-grid">
                                        <button type="submit" className="btn btn-success rounded-pill py-2" disabled={actionLoading}>
                                            {actionLoading ? 'Verifying...' : 'Verify & Enable 2FA'}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        )}
                    </div>

                    <div className="modal-footer bg-light">
                        <button type="button" className="btn btn-secondary btn-sm rounded-pill px-3" onClick={onClose}>
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default TwoFactorModal;
